import { TegLogo } from '@/ui/TegLogo';
import { formatDateLong } from '@/lib/dateFormat';
import { DOC_VERSION, numeroDaSecao } from './printDoc';
import { Sheet } from './PrintBlocks';

export interface TocEntry {
  index: number;
  label: string;
  intro: string;
  /** Página impressa (a capa é a 1) em que a seção abre, calculada pela paginação. */
  page: number;
}

/** Capa do documento: identificação da SMT, título, seções, versão e emissão. Sem rodapé. */
export function PrintCover({ totalSections, emissao }: { totalSections: number; emissao: Date }) {
  return (
    <section className="ds-sheet ds-sheet-cover" data-print-keep-bg="" data-pagina={1}>
      <div className="relative z-10 flex flex-col h-full justify-between">
        <div className="flex items-center gap-4">
          <div className="rounded-2xl bg-primary-foreground p-3 shadow-lg">
            <TegLogo className="h-16 w-16 rounded-xl [&_img]:h-12 [&_img]:w-12" />
          </div>

          <div className="min-w-0">
            <p className="text-base font-bold tracking-tight text-primary-foreground leading-none">
              Sistema TEG
            </p>
            <p className="text-xs text-primary-foreground mt-1.5 leading-snug">
              SMT — Secretaria Municipal de Mobilidade Urbana e Transporte
            </p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-primary-foreground/80 mt-1">
              Prefeitura de São Paulo
            </p>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-foreground/80 mb-3">
            Documentação técnica
          </p>
          <h1 className="text-6xl font-extrabold tracking-tight text-primary-foreground leading-[1.02]">
            Design System
          </h1>
          <div className="h-1.5 w-24 rounded-full bg-primary-foreground/60 mt-5" />
          <p className="text-sm text-primary-foreground/90 mt-5 max-w-md leading-relaxed">
            Padrões visuais, tokens semânticos e componentes reais do Transporte Escolar Gratuito,
            impressos pelo próprio sistema a partir da documentação viva.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 border-t border-primary-foreground/25 pt-5">
          <CoverMeta label="Seções" value={String(totalSections)} />
          <CoverMeta label="Versão" value={DOC_VERSION} />
          <CoverMeta label="Emissão" value={formatDateLong(emissao)} />
        </div>
      </div>
    </section>
  );
}

/** Sumário: uma linha por seção, com a página calculada pela paginação. */
export function Sumario({
  toc,
  pagina,
  total,
}: {
  toc: TocEntry[];
  pagina: number;
  total: number;
}) {
  return (
    <Sheet pagina={pagina} total={total}>
      <h2 className="text-xl font-bold tracking-tight text-foreground border-b border-border pb-3 mb-5">
        Sumário
      </h2>
      <ol className="space-y-3">
        {toc.map((e) => (
          <li key={e.index} className="text-sm">
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-xs text-primary font-semibold w-6 shrink-0">
                {numeroDaSecao(e.index)}
              </span>
              <span className="text-foreground font-medium shrink-0">{e.label}</span>
              <span
                className="flex-1 border-b border-dotted border-border translate-y-[-3px]"
                aria-hidden="true"
              />
              <span className="font-mono text-xs text-muted-foreground shrink-0">{e.page}</span>
            </div>
            <p className="text-xs text-muted-foreground ml-9 mt-0.5">{e.intro}</p>
          </li>
        ))}
      </ol>
    </Sheet>
  );
}

function CoverMeta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-foreground/80">
        {label}
      </p>
      <p className="text-sm font-semibold text-primary-foreground mt-0.5">{value}</p>
    </div>
  );
}
